import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartBarIcon, 
  ClockIcon, 
  ShieldCheckIcon, 
  UsersIcon,
  CheckCircleIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const HomePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
      {/* En-tête */}
      <div className="bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">
                ASPROCOM Vote
              </h1>
              <p className="text-blue-100 text-lg">
                Système de vote électronique sécurisé
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="px-6 py-3 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors duration-200 font-medium"
              >
                Connexion Admin
              </Link>
              <a
                href="/live-tracking"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 font-medium flex items-center"
              >
                <ChartBarIcon className="w-5 h-5 mr-2" />
                Suivi en Direct
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Section héro */}
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-white mb-6">
            Votez en toute sécurité
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Participez aux élections ASPROCOM grâce à notre système de vote électronique 
            moderne, sécurisé et transparent.
          </p>
          <div className="flex justify-center space-x-4">
            <a
              href="/live-tracking"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 font-medium text-lg"
            >
              <ChartBarIcon className="w-6 h-6 mr-3" />
              Suivre les votes en direct
              <ArrowRightIcon className="w-5 h-5 ml-3" />
            </a>
          </div>
        </div>

        {/* Fonctionnalités */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
            <div className="flex items-center mb-4">
              <ShieldCheckIcon className="w-8 h-8 text-green-400 mr-3" />
              <h3 className="text-xl font-semibold text-white">Sécurisé</h3>
            </div>
            <p className="text-blue-100">
              Chaque vote est chiffré et anonyme. Notre système garantit l'intégrité 
              et la confidentialité de vos choix.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
            <div className="flex items-center mb-4">
              <ClockIcon className="w-8 h-8 text-blue-400 mr-3" />
              <h3 className="text-xl font-semibold text-white">Temps Réel</h3>
            </div>
            <p className="text-blue-100">
              Suivez l'évolution des votes en direct. Les résultats sont mis à jour 
              automatiquement toutes les 10 secondes.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
            <div className="flex items-center mb-4">
              <UsersIcon className="w-8 h-8 text-purple-400 mr-3" />
              <h3 className="text-xl font-semibold text-white">Transparent</h3>
            </div>
            <p className="text-blue-100">
              Visualisez les statistiques de participation et les résultats 
              de manière claire et accessible à tous.
            </p>
          </div>
        </div>

        {/* Section suivi en temps réel */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-white mb-4">
              Suivi en Temps Réel
            </h3>
            <p className="text-blue-100 text-lg max-w-2xl mx-auto">
              Découvrez notre interface de suivi en direct qui vous permet de voir 
              l'évolution des votes pour toutes les élections en cours.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h4 className="text-xl font-semibold text-white mb-4">Fonctionnalités</h4>
              <ul className="space-y-3">
                <li className="flex items-center text-blue-100">
                  <CheckCircleIcon className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                  <span>Mise à jour automatique toutes les 10 secondes</span>
                </li>
                <li className="flex items-center text-blue-100">
                  <CheckCircleIcon className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                  <span>Compteurs de temps restant pour chaque élection</span>
                </li>
                <li className="flex items-center text-blue-100">
                  <CheckCircleIcon className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                  <span>Barres de progression visuelles</span>
                </li>
                <li className="flex items-center text-blue-100">
                  <CheckCircleIcon className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                  <span>Mode plein écran pour affichage public</span>
                </li>
                <li className="flex items-center text-blue-100">
                  <CheckCircleIcon className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                  <span>Mode compact pour les très grands écrans</span>
                </li>
              </ul>
            </div>

            <div className="text-center">
              <div className="bg-white/20 rounded-xl p-6 mb-6">
                <ChartBarIcon className="w-16 h-16 text-white mx-auto mb-4" />
                <h5 className="text-lg font-semibold text-white mb-2">
                  Interface Optimisée
                </h5>
                <p className="text-blue-100 text-sm">
                  Conçue pour être visible d'un coup d'œil sur grand écran
                </p>
              </div>
              <a
                href="/live-tracking"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 font-medium"
              >
                <ChartBarIcon className="w-6 h-6 mr-3" />
                Accéder au suivi en direct
                <ArrowRightIcon className="w-5 h-5 ml-3" />
              </a>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-16 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            Comment participer ?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">1</span>
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">Recevez votre lien</h4>
              <p className="text-blue-100 text-sm">
                L'administrateur vous envoie un lien magique personnalisé par email
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">2</span>
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">Votez en ligne</h4>
              <p className="text-blue-100 text-sm">
                Cliquez sur le lien et participez aux élections en cours
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">3</span>
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">Suivez les résultats</h4>
              <p className="text-blue-100 text-sm">
                Consultez le suivi en temps réel pour voir l'évolution des votes
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Pied de page */}
      <div className="bg-white/10 backdrop-blur-sm border-t border-white/20 mt-16">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-blue-100 mb-4">
              Système de vote électronique ASPROCOM
            </p>
            <div className="flex justify-center space-x-6">
              <a
                href="/live-tracking"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-200 hover:text-white transition-colors duration-200"
              >
                Suivi en direct
              </a>
              <Link
                to="/login"
                className="text-blue-200 hover:text-white transition-colors duration-200"
              >
                Administration
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
